import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _compact from 'lodash/compact';
import _map from 'lodash/map';
import _isUndefined from 'lodash/isUndefined';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';

// Components
import EnhanceDropdown from '../../../EnhanceDropdown';
import Button from '../../../Button';
import Form from '../../../Form';

// Router
import { getRoute } from '../../../../entry/routes';

// Actions
import { payOrder, resetCheckoutField } from '../../../../state/modules/order/actions';
import {
  fetchApplePayDeliveryPrice,
  fetchApplePayDeliveryPriceIsNeeded,
  fetchApplePayStatusIsNeeded,
} from '../../../../state/modules/payTypes/actions';

// Icons
import ApplePayIcon from '../../../../icons/applepay.svg';

// Utils
import isomorphicCookies from '../../../../utils/isomorphicCookies';

// Selectors
import { priceIncludingBonusesSelector } from '../../../../state/modules/order/selectors';
import { pickupPricingTotalsSelector } from '../../../../state/modules/pricing/selectors';

// Api
import { post } from '../../../../utils/api';

import styles from './ApplePay.styl';

const cx = classNames.bind(styles);

class ApplePayComponent extends PureComponent {
  static defaultProps = {
    className: '',
    discount: 0,
    usedBonuses: 0,
  };

  static propTypes = {
    className: PropTypes.string,
    history: PropTypes.object,
    price: PropTypes.number,
    priceIncludingBonuses: PropTypes.number,
    discount: PropTypes.number,
    profile: PropTypes.object,
    restaurants: PropTypes.array,
    usedBonuses: PropTypes.number,
    countryCode: PropTypes.string,
    currency: PropTypes.object,
    deliveryCost: PropTypes.number,
    minDeliverySum: PropTypes.number,
    pickupPricing: PropTypes.shape({
      total: PropTypes.number,
      pickupDiscountValue: PropTypes.number,
      isPickupDiscountApplied: PropTypes.bool,
    }),
    dispatch: PropTypes.func,
    intl: PropTypes.object,
  };

  static PAYMENT_LABEL = "Papa John's";

  static SHIPPING_TYPES = ['delivery', 'pickup'];

  static PAYMENT_REQUEST = {
    supportedNetworks: ['masterCard', 'visa'],
    merchantCapabilities: ['supports3DS'],
    displayName: ApplePayComponent.PAYMENT_LABEL,
  };

  state = {
    isLoading: '',
    restaurantId: _get(this.props, 'restaurants[0].id', undefined),
  };

  componentDidMount() {
    this.props.dispatch(fetchApplePayStatusIsNeeded());
    this.props.dispatch(fetchApplePayDeliveryPriceIsNeeded());
  }

  componentWillReceiveProps({ restaurants }) {
    if (restaurants !== this.props.restaurants) {
      this.setState({ restaurantId: _get(restaurants, '[0].id', undefined) });
    }
  }

  componentWillUnmount() {
    this.props.dispatch(resetCheckoutField('error'));
  }

  getUserData = () => {
    const { profile } = this.props;

    return {
      emailAddress: profile.email || '',
      phoneNumber: profile.phone || '',
      givenName: profile.first_name || '',
      familyName: profile.last_name || '',
    };
  };

  getPriceWithPickup = () => {
    const { pickupPricing, priceIncludingBonuses } = this.props;

    return pickupPricing.isPickupDiscountApplied ? pickupPricing.total : priceIncludingBonuses;
  };

  getLineItems = shippingType => {
    const { priceIncludingBonuses, discount, pickupPricing, intl } = this.props;

    if (
      shippingType === ApplePayComponent.SHIPPING_TYPES[1] &&
      pickupPricing.isPickupDiscountApplied
    ) {
      return [
        {
          label: intl.formatMessage({ id: 'applePay.order' }),
          amount: priceIncludingBonuses,
        },
        {
          label: intl.formatMessage({ id: 'pickup.discount' }),
          amount: `-${pickupPricing.pickupDiscountValue}`,
        },
      ];
    }
    const items = [
      {
        label: intl.formatMessage({ id: 'applePay.order' }),
        amount: priceIncludingBonuses,
      },
    ];

    if (discount) {
      items.push({
        label: intl.formatMessage({ id: 'applePay.discount' }),
        amount: discount,
      });
    }

    return items;
  };

  onClickDeliveryButton = () => {
    const shippingType = ApplePayComponent.SHIPPING_TYPES[0];
    this.setState({ isLoading: shippingType });

    const paymentRequest = {
      ...ApplePayComponent.PAYMENT_REQUEST,
      countryCode: this.props.countryCode,
      currencyCode: this.props.currency.code,
      shippingType: ApplePayComponent.SHIPPING_TYPES[0],
      total: {
        label: ApplePayComponent.PAYMENT_LABEL,
        amount: this.props.priceIncludingBonuses,
      },
      lineItems: this.getLineItems(shippingType),
      requiredShippingContactFields: ['name', 'phone', 'postalAddress', 'email'],
      shippingContact: this.getUserData(),
    };
    this.applePayButtonExtendedClicked(paymentRequest, shippingType);
  };

  onClickPickupButton = () => {
    const shippingType = ApplePayComponent.SHIPPING_TYPES[1];
    this.setState({ isLoading: shippingType });

    const restaurants = this.props.restaurants.map(restaurant => {
      return {
        label: restaurant.name,
        amount: 0.0,
        detail: restaurant.address,
        identifier: restaurant.id,
      };
    });
    const paymentRequest = {
      ...ApplePayComponent.PAYMENT_REQUEST,
      countryCode: this.props.countryCode,
      currencyCode: this.props.currency.code,
      shippingType: 'storePickup',
      total: {
        label: ApplePayComponent.PAYMENT_LABEL,
        amount: this.getPriceWithPickup(),
      },
      lineItems: this.getLineItems(shippingType),
      requiredShippingContactFields: ['name', 'phone', 'email'],
      shippingMethods: restaurants,
      shippingContact: this.getUserData(),
    };
    this.applePayButtonExtendedClicked(paymentRequest, shippingType);
  };

  applePayButtonExtendedClicked = (paymentRequest, deliveryType) => {
    const session = new ApplePaySession(3, paymentRequest);

    this.dropdownRef.hide();

    const onSuccess = result => {
      if (result.order_id) {
        session.completePayment({
          status: ApplePaySession.STATUS_SUCCESS,
        });

        isomorphicCookies().remove('thx_link_closed');
        // для отслеживания заказа у неавторизованного пользователя
        isomorphicCookies().set('last_order_id', result.order_id, { expires: 1 });
        // запуск минутного таймера отмены для определенного заказа
        isomorphicCookies().set('cancel_timer', `${result.order_id}|${new Date().getTime()}`, {
          expires: 1,
        });
        this.props.history.push(getRoute('orderTrackingAlias', result.order_id));
      } else {
        const applePayErrors = [];

        _map(result, (error, key) => {
          switch (key) {
            case 'address':
              return applePayErrors.push(
                new ApplePayError('shippingContactInvalid', 'postalAddress', error),
              );
            case 'user_data':
              return _map(result.user_data, (error, key) => {
                switch (key) {
                  case 'name':
                    return applePayErrors.push(
                      new ApplePayError('shippingContactInvalid', 'name', error),
                    );
                  case 'phone':
                    return applePayErrors.push(
                      new ApplePayError('shippingContactInvalid', 'phoneNumber', error),
                    );
                  case 'email':
                    return applePayErrors.push(
                      new ApplePayError('shippingContactInvalid', 'emailAddress', error),
                    );
                  default:
                    return false;
                }
              });
            default:
              return session.abort();
          }
        });

        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [...applePayErrors],
        });

        if (!applePayErrors.length) {
          session.abort();
        }
      }
    };

    session.onvalidatemerchant = event => {
      const data = {
        validation_url: event.validationURL,
      };

      post({
        url: 'apple-pay/start-session',
        data,
      }).then(result => {
        this.setState({ isLoading: '' });

        if (result.Success) {
          session.completeMerchantValidation(result.Model);
        }
      });
    };

    // Изменился адрес самовывоза
    session.onshippingmethodselected = event => {
      this.setState({ restaurantId: event.shippingMethod.identifier });
      session.completeShippingMethodSelection(
        ApplePaySession.STATUS_SUCCESS,
        {
          label: ApplePayComponent.PAYMENT_LABEL,
          amount: this.getPriceWithPickup(),
        },
        this.getLineItems(ApplePayComponent.SHIPPING_TYPES[1]),
      );
    };

    // Изменился адрес доставки
    session.onshippingcontactselected = () => {
      this.props.dispatch(fetchApplePayDeliveryPrice()).then(data => {
        if (!_isUndefined(data.payload.delivery_cost)) {
          const { deliveryCost, price } = this.props;
          const lineItems = this.getLineItems(ApplePayComponent.SHIPPING_TYPES[0]);

          if (deliveryCost) {
            lineItems.splice(1, 0, {
              label: this.props.intl.formatMessage({ id: 'order.delivery.title' }),
              amount: deliveryCost,
            });
          }

          const total = {
            label: ApplePayComponent.PAYMENT_LABEL,
            amount: price + deliveryCost,
          };

          session.completeShippingContactSelection(
            ApplePaySession.STATUS_SUCCESS,
            [],
            total,
            lineItems,
          );
        }
      });
    };

    // Платёж авторизован
    session.onpaymentauthorized = event => {
      const {
        country,
        administrativeArea,
        locality,
        addressLines,
        emailAddress,
        phoneNumber,
        familyName,
        givenName,
      } = event.payment.shippingContact;

      let data = {
        pay_type: 'online',
        cloudpayments_data: {
          apple_pay_cryptogram: JSON.stringify(event.payment.token),
        },
        platform: 'web',
        papabonus_charge: this.props.usedBonuses ? this.props.usedBonuses : undefined,
        user_data: {
          email: emailAddress,
          phone: phoneNumber,
          username: _compact([(familyName, givenName)]).join(' '),
        },
      };

      if (deliveryType === ApplePayComponent.SHIPPING_TYPES[0]) {
        const addressArray = _compact([country, administrativeArea, locality, ...addressLines]);

        data = {
          ...data,
          address_json: {
            address_in_line: addressArray.join(', '),
          },
        };
      } else {
        data = {
          ...data,
          restaurant_id: this.state.restaurantId,
        };
      }

      return this.props.dispatch(payOrder(data, onSuccess));
    };

    session.begin();
  };

  getRef = ref => {
    if (ref) {
      this.dropdownRef = ref;
    }
  };

  render() {
    const { className, minDeliverySum, priceIncludingBonuses, currency } = this.props;
    const { isLoading } = this.state;

    const isInsufficientAmountForDelivery = minDeliverySum > priceIncludingBonuses;

    return (
      <EnhanceDropdown ref={this.getRef}>
        {(isOpen, toggle) => (
          <div className={cx('ApplePay', className)}>
            <Button
              className={cx('ApplePay__button')}
              onClick={this.onClickDeliveryButton}
              label={<FormattedMessage id="button.payHome" />}
              type="button"
              dark
            >
              <FormattedMessage id="button.buyWith" />
              <ApplePayIcon />
            </Button>
            <div className={cx('ApplePay__tooltip', { ApplePay__tooltip_visible: isOpen })}>
              <div className={cx('ApplePay__tooltipContent')}>
                <div className={cx('ApplePay__tooltipText')}>
                  <FormattedMessage id="applePay.chooseDeliveryType" />
                </div>
                <Button
                  className={cx('ApplePay__tooltipButton')}
                  isLoading={isLoading === ApplePayComponent.SHIPPING_TYPES[0]}
                  disabled={isInsufficientAmountForDelivery}
                  onClick={this.onClickDeliveryButton}
                >
                  <FormattedMessage id="order.delivery.title" />
                </Button>
                <Button
                  className={cx('ApplePay__tooltipButton')}
                  isLoading={isLoading === ApplePayComponent.SHIPPING_TYPES[1]}
                  onClick={this.onClickPickupButton}
                  dark
                >
                  <FormattedMessage id="order.pickup.title" />
                </Button>
                <FormattedMessage
                  id="checkout.minimal.delivery"
                  values={{
                    minSum: minDeliverySum,
                    unit: currency.unit,
                  }}
                >
                  {error => <Form.Error error={isInsufficientAmountForDelivery ? error : ''} />}
                </FormattedMessage>
              </div>
            </div>
          </div>
        )}
      </EnhanceDropdown>
    );
  }
}

const connector = connect(
  state => ({
    profile: _get(state, 'user.profile', {}),
    restaurants: _filter(_get(state, 'restaurant.list', []), item => {
      return item.pay_params.apple_pay === 1;
    }),
    countryCode: _get(state, 'city.userCity.country_code', ''),
    currency: _get(state, 'city.userCity.currency', ''),
    deliveryCost: _get(state, 'payTypes.applePayDeliveryPrice.delivery_costs', 0),
    minDeliverySum: _get(state, 'payTypes.applePayDeliveryPrice.min_sum', 0),
    usedBonuses: state.sailplay.usedBonuses,
    priceIncludingBonuses: priceIncludingBonusesSelector(state),
    pickupPricing: pickupPricingTotalsSelector(state),
  }),
  null,
);

export default connector(injectIntl(withRouter(ApplePayComponent)));
